import { z } from 'zod';

// ========== HELPER SCHEMAS ==========

// Date format: YYYY-MM-DD
const dateStringSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (expected YYYY-MM-DD)');

// Time format: HH:MM
const timeStringSchema = z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Invalid time format (expected HH:MM)');

// Safe positive integer
const safePositiveInt = z.number().int().nonnegative();

// Safe positive number
const safePositiveNumber = z.number().nonnegative();

// ========== ENUM SCHEMAS ==========

export const questTypeSchema = z.enum(['daily', 'main', 'instant', 'boss']);
export const questDifficultySchema = z.enum(['easy', 'normal', 'hard', 'boss']);
export const questStatusSchema = z.enum(['active', 'completed', 'failed']);
export const programIntensitySchema = z.enum(['light', 'moderate', 'intense']);
export const programStatusSchema = z.enum(['active', 'completed', 'paused']);
export const programDomainSchema = z.enum(['learning', 'fitness', 'creative', 'habits', 'custom']);
export const passiveSkillCategorySchema = z.enum(['physical', 'mental', 'social', 'spiritual', 'productivity']);
export const passiveSkillFrequencySchema = z.enum(['daily', 'weekly', 'custom']);

// ========== CORE DATA SCHEMAS ==========

export const statsSchema = z.object({
  strength: safePositiveInt,
  intelligence: safePositiveInt,
  agility: safePositiveInt,
  vitality: safePositiveInt,
  sense: safePositiveInt,
});

export const partialStatsSchema = statsSchema.partial();

export const subTaskSchema = z.object({
  id: z.string(),
  text: z.string().min(1).max(500),
  completed: z.boolean(),
});

// ========== QUEST VALIDATION ==========

export const questCreateSchema = z.object({
  title: z.string()
    .min(3, 'Title must be at least 3 characters')
    .max(200, 'Title must be less than 200 characters')
    .trim(),
  description: z.string()
    .max(2000, 'Description must be less than 2000 characters')
    .trim(),
  type: questTypeSchema,
  difficulty: questDifficultySchema,
  resetDaily: z.boolean().optional().default(false),
  scheduledFor: dateStringSchema.optional(),
  scheduledTime: z.object({
    start: timeStringSchema,
    end: timeStringSchema,
  }).optional(),
  allDay: z.boolean().optional(),
  estimatedDuration: safePositiveInt.max(1440).optional(), // Max 24 hours
  subTasks: z.array(subTaskSchema).max(20).optional(), // Max 20 subtasks
  customColor: z.string().max(50).optional(),
});

export const questUpdateSchema = z.object({
  title: z.string()
    .min(3)
    .max(200)
    .trim()
    .optional(),
  description: z.string()
    .max(2000)
    .trim()
    .optional(),
  status: questStatusSchema.optional(),
  scheduledFor: dateStringSchema.optional(),
  scheduledTime: z.object({
    start: timeStringSchema,
    end: timeStringSchema,
  }).optional(),
  allDay: z.boolean().optional(),
  estimatedDuration: safePositiveInt.max(1440).optional(),
  subTasks: z.array(subTaskSchema).max(20).optional(),
  customColor: z.string().max(50).optional(),
});

// ========== PROGRAM VALIDATION ==========

export const programCreateSchema = z.object({
  goal: z.string()
    .min(5, 'Goal must be at least 5 characters')
    .max(500, 'Goal must be less than 500 characters')
    .trim(),
  duration: z.number()
    .int()
    .min(1, 'Duration must be at least 1 day')
    .max(365, 'Duration cannot exceed 365 days'),
  scheduleType: z.enum(['daily', 'every_other_day', '2x_week', '3x_week', '5x_week', 'weekends', 'custom']),
  customDays: z.array(z.number().int().min(0).max(6)).optional(),
  questsPerDay: z.number()
    .int()
    .min(1, 'Must have at least 1 quest per day')
    .max(10, 'Maximum 10 quests per day'),
  intensity: programIntensitySchema,
  domain: programDomainSchema,
  domainAnswers: z.record(z.string(), z.union([z.string(), z.array(z.string()), z.number()])).optional(),
  aiFollowUpAnswers: z.record(z.string(), z.string()).optional(),
  skillLevel: z.enum(['beginner', 'intermediate', 'advanced']).optional(),
  timePerDay: safePositiveInt.max(600).optional(), // Max 10 hours
  learningStyle: z.string().max(200).trim().optional(),
  specificFocus: z.string().max(500).trim().optional(),
  availableResources: z.string().max(500).trim().optional(),
});

export const programUpdateSchema = z.object({
  status: programStatusSchema.optional(),
  progress: z.number().min(0).max(100).optional(),
  currentPhase: safePositiveInt.optional(),
});

// ========== PASSIVE SKILL VALIDATION ==========

export const passiveSkillCreateSchema = z.object({
  name: z.string()
    .min(3, 'Name must be at least 3 characters')
    .max(200, 'Name must be less than 200 characters')
    .trim(),
  description: z.string()
    .max(1000, 'Description must be less than 1000 characters')
    .trim(),
  category: passiveSkillCategorySchema,
  icon: z.string().max(50),
  frequency: passiveSkillFrequencySchema,
  targetDaysPerWeek: z.number()
    .int()
    .min(1)
    .max(7)
    .optional(),
  statBoost: z.object({
    stat: z.enum(['strength', 'intelligence', 'agility', 'vitality', 'sense']),
    percentBoost: z.number()
      .min(0.01, 'Boost must be at least 1%')
      .max(0.5, 'Boost cannot exceed 50%'),
  }),
});

export const passiveSkillUpdateSchema = z.object({
  name: z.string()
    .min(3)
    .max(200)
    .optional()
    .transform(val => val?.trim()),
  description: z.string()
    .max(1000)
    .optional()
    .transform(val => val?.trim()),
  targetDaysPerWeek: z.number()
    .int()
    .min(1)
    .max(7)
    .optional(),
  isActive: z.boolean().optional(),
});

// ========== USER PROFILE VALIDATION ==========

export const userProfileUpdateSchema = z.object({
  name: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters')
    .trim()
    .optional(),
  level: z.number()
    .int()
    .min(1)
    .max(999)
    .optional(),
  currency: z.number()
    .int()
    .min(0)
    .max(999999999)
    .optional(),
  currentXP: safePositiveInt.optional(),
  totalXP: safePositiveInt.optional(),
  stats: statsSchema.optional(),
  passiveStatBoosts: partialStatsSchema.optional(),
  unallocatedStatPoints: safePositiveInt.max(9999).optional(),
});

// ========== STAT ALLOCATION VALIDATION ==========

export const statAllocationSchema = partialStatsSchema.refine(
  (data) => {
    const total = Object.values(data).reduce((sum, val) => sum + (val || 0), 0);
    return total > 0 && total <= 100; // Max 100 points at once
  },
  { message: 'Total stat points must be between 1 and 100' }
);

// ========== RATE LIMITING ==========

interface RateLimitConfig {
  action: string;
  maxRequests: number;
  windowMs: number; // Time window in milliseconds
}

const rateLimitStore = new Map<string, { count: number; resetAt: number }>();

/**
 * Client-side rate limiting check
 * @param userId - User ID or session ID
 * @param config - Rate limit configuration
 * @returns true if request is allowed, false if rate limited
 */
export function checkRateLimit(userId: string, config: RateLimitConfig): boolean {
  const key = `${userId}:${config.action}`;
  const now = Date.now();
  const stored = rateLimitStore.get(key);

  // No previous requests or window expired
  if (!stored || now > stored.resetAt) {
    rateLimitStore.set(key, {
      count: 1,
      resetAt: now + config.windowMs,
    });
    return true;
  }

  // Within rate limit
  if (stored.count < config.maxRequests) {
    stored.count++;
    return true;
  }

  // Rate limited
  return false;
}

/**
 * Get time until rate limit resets
 * @param userId - User ID or session ID
 * @param action - Action name
 * @returns milliseconds until reset, or 0 if not rate limited
 */
export function getRateLimitReset(userId: string, action: string): number {
  const key = `${userId}:${action}`;
  const stored = rateLimitStore.get(key);
  
  if (!stored) return 0;
  
  const now = Date.now();
  if (now > stored.resetAt) return 0;
  
  return stored.resetAt - now;
}

// ========== VALIDATION (NO CLIENT-SIDE SANITIZATION) ==========

/**
 * Validate user input with Zod schema
 * 
 * NOTE: This function only validates structure, types, and formats.
 * It does NOT sanitize for security (XSS, SQL injection, etc.)
 * 
 * Security sanitization happens on the server via Cloud Functions.
 * Client-side sanitization provides no security (can be bypassed).
 * 
 * @param schema - Zod schema to validate against
 * @param data - Data to validate
 * @returns Validation result with validated data or error
 */
export function validateInput<T>(
  schema: z.ZodSchema<T>,
  data: unknown
): { success: true; data: T } | { success: false; error: string } {
  try {
    // ✅ ONLY VALIDATE with Zod (structure, types, formats)
    // ❌ NO client-side sanitization (security is server-side only)
    const result = schema.safeParse(data);
    
    if (result.success) {
      return { success: true, data: result.data };
    } else {
      // Format Zod errors nicely
      const errorMessage = result.error.issues
        .map((err: z.ZodIssue) => `${err.path.join('.')}: ${err.message}`)
        .join('; ');
      
      return { success: false, error: errorMessage };
    }
  } catch (error) {
    return { success: false, error: 'Validation failed: Invalid input' };
  }
}


// ========== PRESETS FOR COMMON VALIDATIONS ==========

export const rateLimitPresets = {
  questCreate: { action: 'quest_create', maxRequests: 20, windowMs: 60000 }, // 20 per minute
  questUpdate: { action: 'quest_update', maxRequests: 60, windowMs: 60000 }, // 60 per minute
  questTimeBlock: { action: 'quest_time_block', maxRequests: 200, windowMs: 60000 }, // 200 per minute - more lenient for calendar adjustments
  programCreate: { action: 'program_create', maxRequests: 5, windowMs: 300000 }, // 5 per 5 minutes
  aiGenerate: { action: 'ai_generate', maxRequests: 10, windowMs: 60000 }, // 10 per minute
  passiveSkillCreate: { action: 'passive_create', maxRequests: 10, windowMs: 60000 }, // 10 per minute
  profileUpdate: { action: 'profile_update', maxRequests: 30, windowMs: 60000 }, // 30 per minute
};

// ========== EXPORTS ==========

export type QuestCreateInput = z.infer<typeof questCreateSchema>;
export type QuestUpdateInput = z.infer<typeof questUpdateSchema>;
export type ProgramCreateInput = z.infer<typeof programCreateSchema>;
export type ProgramUpdateInput = z.infer<typeof programUpdateSchema>;
export type PassiveSkillCreateInput = z.infer<typeof passiveSkillCreateSchema>;
export type PassiveSkillUpdateInput = z.infer<typeof passiveSkillUpdateSchema>;
export type UserProfileUpdateInput = z.infer<typeof userProfileUpdateSchema>;
export type StatAllocationInput = z.infer<typeof statAllocationSchema>;

